import Button from '@mui/material/Button';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Size as DeviceSize, FeatureFlag } from '@ps/web-core-enums';
import { select, useFeature } from '@ps/web-core-store';
import { StarsButton, useScreenSize } from '@ps/web-shared-components';
import React, { useContext, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';

import { Context as NavigationContext } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import useWizardContext from '@swf/core/hooks/useWizardContext';

import CloseIcon from '../../../../../../assets/icons/close.svg';
import PreviousIcon from '../../../../../../assets/icons/previous.svg';
import WelcomeMobileIcon from '../../../../../../assets/welcome-mobile.jpg';
import WelcomeUkIcon from '../../../../../../assets/welcome-uk.jpg';
import WelcomeIcon from '../../../../../../assets/welcome.jpg';
import ProgressBar from '../../../../../ProgressBar/ProgressBar';
import ResponsinbleGamingImage from '../../../../../ResponsibleGamingImage/ResponsibleGamingImage';
import SignupCountryImageByLicence from '../../../../../SignupCountryImageByLicense/SignupCountryImageByLicence';
import BrandLogo from '../../BrandLogo/BrandLogo';
import type { Buttons } from '../../Header';

import { backButton, closeButton, header, image, staticHeader } from './WizardHeader.scss';

interface Props {
  buttons?: Buttons;
  backButtonDisabled: boolean;
  withImage?: boolean;
  showSGImage?: boolean;
  modal?: boolean;
  hideProgressBar?: boolean;
  title: React.ReactNode;
  handleExitPrompt: () => void;
  handleCloseClick: () => void;
  handleBackClick: () => void;
}

const WizardHeader: React.FC<Props> = ({
  buttons,
  backButtonDisabled,
  withImage,
  showSGImage,
  modal,
  hideProgressBar = false,
  title,
  handleExitPrompt,
  handleCloseClick,
  handleBackClick
}) => {
  const { previous } = useContext(NavigationContext);
  const headerRef = useRef<HTMLHeadingElement>(null);
  const exitFlowEnabled = useFeature(FeatureFlag.SwcExitFlow);
  const isNewSingupCountryImageEnabled = useFeature(FeatureFlag.SwcNewSignupCountryImage);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isStandAloneRmfMandatory = useSelector(select.account.isStandAloneRmfMandatory);
  const siteId = useSelector(select.cms.getSiteId);
  const isUk = siteId === Sites.UK;

  const {
    data: { cmsHeaderAndGridLayout }
  } = useWizardContext();

  const showProgressBar =
    useFeature(FeatureFlag.SwcOnboardingProgressbar) &&
    !cmsHeaderAndGridLayout &&
    !isStandAloneRmfMandatory &&
    !hideProgressBar;
  const { greaterThan } = useScreenSize();

  return (
    <>
      <Helmet>{headerRef.current && <title>{headerRef.current.innerText}</title>}</Helmet>
      <>
        {!isEmbedded && (
          <div className={staticHeader}>
            <main>
              {exitFlowEnabled ? (
                <Button onClick={handleExitPrompt} style={{ padding: 0 }}>
                  <BrandLogo />
                </Button>
              ) : (
                <BrandLogo />
              )}
            </main>
          </div>
        )}
        <header className={header}>
          <main>
            {withImage && !isNewSingupCountryImageEnabled && (
              <img
                alt="Welcome"
                className={image}
                src={
                  isUk
                    ? WelcomeUkIcon
                    : greaterThan(DeviceSize.XSmall)
                    ? WelcomeIcon
                    : WelcomeMobileIcon
                }
              />
            )}
            {!buttons?.hideCloseButton && (
              <StarsButton
                icon={modal ? <PreviousIcon /> : <CloseIcon />}
                onClick={handleCloseClick}
                className={modal ? backButton : closeButton}
              />
            )}
            {((buttons?.back && !modal) || (!buttons && previous)) && (
              <StarsButton
                icon={<PreviousIcon />}
                disabled={backButtonDisabled}
                onClick={handleBackClick}
                className={backButton}
              />
            )}
            <h2 ref={headerRef}>{title}</h2>
          </main>
        </header>
        {showProgressBar && <ProgressBar />}
        {showSGImage && <ResponsinbleGamingImage />}
        {withImage && isNewSingupCountryImageEnabled && <SignupCountryImageByLicence />}
      </>
    </>
  );
};
export default WizardHeader;
